/**
 * The whole workflow, on one screen: review on the left, map on the right.
 *
 * The plugin puts the dock beside the QGIS canvas and this puts the same two
 * things side by side, because the job is the same job. A reviewer reads a
 * verdict, looks at the courses it was computed from, places the tract, and
 * takes an artifact away.
 *
 * State lives here rather than in a store: there is one document in flight at
 * a time, and a reducer would be ceremony around four `useState` calls.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { Api, ApiError, type TokenSource } from "./api";
import { Auth, PastedKeyAuth, type AuthState } from "./auth";
import { Cancelled, runBatch, runExtract, runExtractText } from "./jobs";
import { Review, type CorrectionRequest } from "./review/Review";
import { Triage } from "./review/Triage";
import { MapView } from "./map/MapView";
import { SignIn } from "./auth/SignIn";
import { Signup } from "./auth/Signup";
import { Admin } from "./admin/Admin";
import { Keys } from "./keys/Keys";
import { Runs } from "./runs/Runs";
import { baseName, download, type PlacedTract } from "./exports";
import {
  georefRings,
  isPlaceable,
  pobOf,
  singleRing,
  type LngLat,
  type Ring,
} from "./place";
import type {
  HeldResponse,
  RunRow,
  SavedPlacement,
  ServerVerdict,
  Tract,
} from "./types";

const auth = new Auth(
  { userPoolId: __COGNITO_USER_POOL_ID__, clientId: __COGNITO_CLIENT_ID__ },
  () => {},
);
const devAuth = new PastedKeyAuth(() => {});

/** Three paths beside the product, matched by prefix. Still not a router:
 *  four screens do not need one either, and CloudFront already rewrites
 *  extensionless paths to index.html so a hard load of /admin, /runs or /keys
 *  serves the app. */
const ADMIN_PATH = "/admin";
const RUNS_PATH = "/runs";
const KEYS_PATH = "/keys";
// The one path that works signed out, because it is how you come to have an
// account at all.
const SIGNUP_PATH = "/signup";

export function App() {
  const [signedIn, setSignedIn] = useState(false);
  // True until restore() has answered, so a refresh mid-session shows a blank
  // beat instead of a sign-in page that immediately swaps away.
  const [restoring, setRestoring] = useState(auth.configured);
  const [superadmin, setSuperadmin] = useState(false);
  const [path, setPath] = useState(
    typeof location === "undefined" ? "/" : location.pathname,
  );
  const [held, setHeld] = useState<HeldResponse | null>(null);
  const [batch, setBatch] = useState<HeldResponse[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Ring[] | null>(null);
  const [pasting, setPasting] = useState(false);
  const [pob, setPob] = useState<LngLat | null>(null);
  const [rotation, setRotation] = useState(0);
  const [placed, setPlaced] = useState<PlacedTract[]>([]);
  const [locating, setLocating] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  // Attribution for the correction log. Recorded, never verified — the
  // service knows which key called it, not which person typed.
  const [email, setEmail] = useState("");
  // Null until the service has been asked; a number afterwards, including
  // zero, which is the ordinary answer and worth saying out loud.
  const [located, setLocated] = useState<number | null>(null);
  // Where the last locate call put things, so the map can go and show them.
  const [focus, setFocus] = useState<LngLat[] | null>(null);
  // The run on screen, as the service names it, and whether that reference
  // covers more than this one document. Placements are saved against it, so
  // null means nothing to save against yet.
  const [reference, setReference] = useState<{
    id: string;
    batch: boolean;
  } | null>(null);
  const abort = useRef<AbortController | null>(null);

  const source: TokenSource = useMemo(
    () => ({
      token: () => (auth.configured ? auth.token() : devAuth.token()),
      onUnauthorized: () => setSignedIn(false),
    }),
    [],
  );
  const api = useMemo(() => new Api(source), [source]);

  const placedNames = useMemo(
    () => new Set(placed.map((item) => item.ring.tract.name)),
    [placed],
  );

  const onSignedIn = useCallback((_state: AuthState) => setSignedIn(true), []);

  // Pick the session back up on a reload. The SDK already holds it; without
  // this a refresh inside the token's lifetime lands on the sign-in page.
  useEffect(() => {
    if (!auth.configured) return;
    let live = true;
    auth
      .restore()
      .then((ok) => {
        if (!live) return;
        if (ok) setSignedIn(true);
        setRestoring(false);
      })
      .catch(() => live && setRestoring(false));
    return () => {
      live = false;
    };
  }, []);

  // Who is signed in, asked once. The console is hidden without the group
  // claim, and the server refuses every one of its routes regardless, so this
  // decides what to draw rather than what is allowed.
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    api
      .whoami()
      .then((me) => {
        if (!live) return;
        setSuperadmin(!!me.superadmin);
        setEmail(me.email ?? "");
      })
      .catch(() => live && setSuperadmin(false));
    return () => {
      live = false;
    };
  }, [signedIn, api]);

  // Back and forward still work, because the console is a real URL.
  useEffect(() => {
    const onPop = () => setPath(location.pathname);
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((to: string) => {
    history.pushState(null, "", to);
    setPath(to);
  }, []);

  const reset = () => {
    setHeld(null);
    // The batch was never cleared here, so extracting a single document
    // after a folder run left the previous batch's list on screen above the
    // new result. It was survivable while that list was a row of chips and
    // obvious once it became a triage queue carrying reasons for documents
    // nobody had just run.
    setBatch([]);
    setPlaced([]);
    setLocated(null);
    setFocus(null);
    setPending(null);
    setPob(null);
    setRotation(0);
    setError(null);
    setReference(null);
  };

  async function extract(file: File) {
    reset();
    const controller = new AbortController();
    abort.current = controller;
    setBusy("Submitting…");
    try {
      const { job, held: result } = await runExtract(
        api,
        file,
        controller.signal,
        (message, seconds) => setBusy(`${message} ${seconds}s`),
      );
      setReference({ id: job.job, batch: false });
      setHeld(result);
      setLocated(null);
      setFocus(null);
      // An aliquot tract is already located, so it lands without a click. The
      // plugin does the same, and for the same reason: the PLSS fabric
      // supplies the position, not the operator.
      autoPlaceGeoref(result);
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setBusy(null);
      abort.current = null;
    }
  }

  async function extractText(text: string, docName: string) {
    setPasting(false);
    reset();
    const controller = new AbortController();
    abort.current = controller;
    setBusy("Submitting…");
    try {
      const { job, held: result } = await runExtractText(
        api,
        text,
        docName,
        controller.signal,
        (message, seconds) => setBusy(`${message} ${seconds}s`),
      );
      setReference({ id: job.job, batch: false });
      setHeld(result);
      setLocated(null);
      setFocus(null);
      autoPlaceGeoref(result);
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setBusy(null);
      abort.current = null;
    }
  }

  function autoPlaceGeoref(result: HeldResponse) {
    const tracts = result.parsed.extraction.tracts ?? [];
    const located: PlacedTract[] = [];
    tracts.forEach((tract, index) => {
      const verdict = result.parsed.groundtruth_verdicts[index];
      const rings = georefRings(verdict);
      if (rings.length) {
        located.push({
          ring: singleRing(tract, verdict),
          rotationDeg: 0,
          georefRings: rings,
        });
      }
    });
    if (located.length) setPlaced(located);
  }

  async function extractBatch(files: File[]) {
    reset();
    const controller = new AbortController();
    abort.current = controller;
    try {
      const results = await runBatch(
        api,
        files,
        controller.signal,
        (status, seconds) => {
          // The batch id arrives with the first progress report, and it is
          // the reference every placement in this folder is saved against.
          setReference({ id: status.batch, batch: true });
          setBusy(
            `Batch ${status.status} · ${status.documents} documents · ${seconds}s`,
          );
        },
      );
      setBatch(results);
      if (results[0]) {
        setHeld(results[0]);
        setLocated(null);
        setFocus(null);
        autoPlaceGeoref(results[0]);
      }
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setBusy(null);
      abort.current = null;
    }
  }

  function startPlacing(rings: Ring[]) {
    setPending(rings.filter((ring) => isPlaceable(ring.verdict)));
    setPob(null);
    setRotation(0);
  }

  /**
   * Reopen a run whose result the API still holds.
   *
   * The ordinary poll, nothing more: `/v1/extracts/{job}` returns the stored
   * response and starts the retention clock exactly as it does the first
   * time. Nothing is resurrected here. A run whose result the sweep has taken
   * offers no button at all, and if one is taken between the listing and the
   * click the server answers 410 with the window that explains it, which is a
   * better sentence than any this file could write.
   */
  async function openRun(run: RunRow) {
    go("/");
    reset();
    setBusy("Opening " + run.document);
    setReference({ id: run.reference, batch: run.source === "batch" });
    try {
      if (run.source === "batch") {
        // A batch collects whole or not at all: the results route serves
        // every document in it. That is the route's bargain rather than a
        // choice made here, so the whole batch comes back into the portal
        // with its triage queue and this document selected, which is more
        // useful than one document on its own would have been.
        const results = await api.batchResults(run.reference);
        if (!results.length) {
          setError("That batch has no stored results to open.");
          return;
        }
        setBatch(results);
        const wanted =
          results.find((item) => item.parsed.document === run.document) ??
          results[0];
        setHeld(wanted);
        autoPlaceGeoref(wanted);
        await restorePlacements(run.reference, wanted);
        return;
      }
      const { held: reopened } = await api.pollExtract(run.reference);
      if (!reopened) {
        setError("That run has no stored result to open.");
        return;
      }
      setHeld(reopened);
      autoPlaceGeoref(reopened);
      await restorePlacements(run.reference, reopened);
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Ask the service where these tracts sit, and take back the whole re-signed
   * response.
   *
   * The suggestions cannot be spliced into what is already held: the
   * attestation covers the body as a unit, so a spliced payload is one the
   * certificate refuses. What comes back replaces what was held, raw bytes
   * included.
   */
  async function suggestLocations() {
    if (!held) return;
    setLocating(true);
    try {
      const answer = await api.locate(held);
      setHeld(answer.held);
      setLocated(answer.located);
      // Take the camera to the suggestions. Nothing to show means the map
      // stays where the operator left it, rather than flying off to nowhere.
      const points = (answer.held.parsed.groundtruth_verdicts ?? [])
        .map((verdict) => verdict.location?.pob)
        .filter((pob): pob is [number, number] => !!pob)
        .map(([lng, lat]) => ({ lng, lat }));
      setFocus(points.length ? points : null);
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setLocating(false);
    }
  }

  /**
   * Send a correction, or a request to close a tract, and hold what comes
   * back.
   *
   * Two acts down one call, and the service keeps them apart: a correction is
   * a better reading of the document and the verdicts come back recomputed
   * over it; closing a shape fits geometry and the verdict deliberately does
   * not move. Neither is applied here — a payload edited in the browser is one
   * the certificate refuses, which is the whole reason this is a round trip.
   *
   * Placement is reset because the geometry underneath it may have changed. A
   * tract left drawn at its pre-correction shape would be the stalest thing on
   * the screen and the one most likely to be exported.
   */
  async function correct(change: CorrectionRequest) {
    if (!held) return;
    setCorrecting(true);
    setError(null);
    try {
      const answer = await api.correct(held, {
        corrections: (change.changes ?? []).map((edit) => ({
          tract: change.tract,
          call: change.call ?? 0,
          kind: change.kind ?? "boundary",
          field: edit.field,
          before: edit.before,
          after: edit.after,
          note: change.note ?? "",
        })),
        adjust: change.close ? [change.tract] : [],
        by: email,
      });
      setHeld(answer.held);
      setPlaced([]);
      setPending(null);
      setPob(null);
    } catch (failure) {
      setError(describe(failure));
    } finally {
      setCorrecting(false);
    }
  }

  /**
   * Start the ordinary placement flow with the point of beginning already
   * dropped at the suggestion.
   *
   * Deliberately the same pending-and-confirm path a click produces. The
   * operator sees the parcel where the service thinks it goes, adjusts
   * rotation, and confirms it themselves; a suggestion that placed itself
   * would be the auto-accept this product refuses.
   */
  function placeSuggested(tract: Tract, verdict: ServerVerdict) {
    const suggested = verdict.location?.pob;
    if (!suggested) return;
    const ring = singleRing(tract, verdict);
    if (!isPlaceable(ring.verdict)) return;
    setPending([ring]);
    setPob({ lng: suggested[0], lat: suggested[1] });
    setRotation(0);
  }

  function placeGeoref(tract: Tract, verdict: ServerVerdict) {
    const rings = georefRings(verdict);
    if (!rings.length) return;
    setPlaced((current) => [
      ...current,
      { ring: singleRing(tract, verdict), rotationDeg: 0, georefRings: rings },
    ]);
  }

  /**
   * Draw a reopened run's tracts where somebody put them before.
   *
   * The point of saving a placement at all: a deed located last Tuesday opens
   * on the map rather than at the origin, in the browser or in QGIS,
   * whichever placed it.
   *
   * Stored positions are added to whatever `autoPlaceGeoref` already drew
   * rather than replacing it, because those two never overlap: an aliquot
   * tract is placed by the fabric and is never saved here.
   */
  async function restorePlacements(id: string, result: HeldResponse) {
    const tracts = result.parsed.extraction?.tracts ?? [];
    const verdicts = result.parsed.groundtruth_verdicts ?? [];
    const document = result.parsed.document ?? "";
    let saved: SavedPlacement[];
    try {
      saved = await api.placements(id);
    } catch {
      return; // the result is open and placeable by hand; nothing is lost
    }
    const restored: PlacedTract[] = [];
    for (const position of saved) {
      if (position.document && position.document !== document) continue;
      const tract = tracts[position.tract_index];
      const verdict = verdicts[position.tract_index];
      if (!tract || !verdict) continue; // stored against a different shape
      restored.push({
        ring: singleRing(tract, verdict),
        origin: { lng: position.lon, lat: position.lat },
        rotationDeg: position.rotation_deg,
      });
    }
    if (restored.length) setPlaced((current) => [...current, ...restored]);
  }

  /**
   * Keep the placement, and tell the service where it went.
   *
   * Each tract is saved at its own point of beginning rather than at the
   * shared monument a group click used, because restoring from the monument
   * alone would stack the whole assembly on one spot. Coordinates go as
   * WGS 84 so a parcel placed here opens in the same field in QGIS.
   *
   * The save is not awaited and a failure is not raised. The parcel is on the
   * map either way; what a failure costs is the recall, not the work.
   */
  function confirmPlacement() {
    if (!pending || !pob) return;
    const tracts = held?.parsed.extraction?.tracts ?? [];
    const positions: SavedPlacement[] = pending.flatMap((ring) => {
      const index = tracts.indexOf(ring.tract);
      if (index < 0) return [];
      const origin = pobOf(ring, pob, rotation);
      return [
        {
          tract_index: index,
          lon: origin.lng,
          lat: origin.lat,
          rotation_deg: rotation,
        },
      ];
    });
    setPlaced((current) => [
      ...current,
      ...pending.map((ring) => ({ ring, origin: pob, rotationDeg: rotation })),
    ]);
    setPending(null);
    setPob(null);
    setRotation(0);
    if (reference && positions.length) {
      void api
        .savePlacements(
          reference.id,
          positions,
          // A folder run is one reference over many deeds, so the name says
          // which. A single extraction needs none: the service knows it.
          reference.batch ? (held?.parsed.document ?? "") : "",
        )
        .catch(() => {});
    }
  }

  async function saveCertificate() {
    if (!held) return;
    try {
      download(
        await api.certificate(held),
        `${baseName(held)}-certificate.pdf`,
      );
    } catch (failure) {
      setError(describe(failure));
    }
  }

  async function saveDxf() {
    if (!held) return;
    try {
      download(await api.dxf(held), `${baseName(held)}.dxf`);
    } catch (failure) {
      setError(describe(failure));
    }
  }

  async function saveGeoPackage() {
    if (!held) return;
    try {
      download(await api.geopackage(held), `${baseName(held)}.gpkg`);
    } catch (failure) {
      setError(describe(failure));
    }
  }

  if (restoring) {
    return null;
  }

  if (!signedIn) {
    if (path.startsWith(SIGNUP_PATH)) {
      // Checked before the sign-in page, not after: somebody arriving here has
      // no credential yet, and sending them to a login form is exactly the
      // dead end this replaced.
      return (
        <Signup
          onDone={() => {
            history.pushState({}, "", "/");
            setPath("/");
          }}
        />
      );
    }
    return <SignIn auth={auth} devAuth={devAuth} onSignedIn={onSignedIn} />;
  }

  if (path.startsWith(RUNS_PATH)) {
    return (
      <div class="shell">
        <header class="topbar">
          <a class="wordmark" href="https://easting.ai">
            easting<span>.ai</span>
          </a>
          <span class="badge">Run history</span>
          <div class="grow" />
          <button type="button" class="ghost" onClick={() => go("/")}>
            Back to extraction
          </button>
        </header>
        <Runs api={api} onOpen={openRun} onExit={() => go("/")} />
      </div>
    );
  }

  if (path.startsWith(KEYS_PATH)) {
    return (
      <div class="shell">
        <header class="topbar">
          <a class="wordmark" href="https://easting.ai">
            easting<span>.ai</span>
          </a>
          <span class="badge">API keys</span>
          <div class="grow" />
          <button type="button" class="ghost" onClick={() => go("/")}>
            Back to extraction
          </button>
        </header>
        <Keys api={api} onExit={() => go("/")} />
      </div>
    );
  }

  if (path.startsWith(ADMIN_PATH)) {
    // A customer who types the URL gets the product, not a 403 page. The
    // routes behind it refuse them either way.
    if (!superadmin) {
      go("/");
    } else {
      return (
        <div class="shell">
          <header class="topbar">
            <a class="wordmark" href="https://easting.ai">
              easting<span>.ai</span>
            </a>
            <span class="badge">Operator console</span>
            <div class="grow" />
            <button
              type="button"
              class="ghost"
              onClick={() => (
                auth.configured ? auth.signOut() : devAuth.signOut(),
                setSignedIn(false)
              )}
            >
              Sign out
            </button>
          </header>
          <Admin api={api} onExit={() => go("/")} />
        </div>
      );
    }
  }

  const anyPlaceable = (held?.parsed.groundtruth_verdicts ?? []).some(
    isPlaceable,
  );
  // Only offered where it could work: an aliquot tract is already located by
  // the fabric, and a tract with no traverse has nothing to contain.
  const anyLocatable = (held?.parsed.groundtruth_verdicts ?? []).some(
    (verdict) => isPlaceable(verdict) && !verdict.georef?.rings?.length,
  );

  return (
    <div class="shell">
      <header class="topbar">
        <a class="wordmark" href="https://easting.ai">
          easting<span>.ai</span>
        </a>
        <FilePicker
          onExtract={extract}
          onBatch={extractBatch}
          onPaste={() => setPasting(true)}
          disabled={!!busy}
        />
        <div class="grow" />
        <button
          type="button"
          class="ghost"
          onClick={() => go(RUNS_PATH)}
          title="Every document this account has run, and whether its result is still held."
        >
          History
        </button>
        <button
          type="button"
          class="ghost"
          onClick={() => go(KEYS_PATH)}
          title="The account's API keys: rotate a lost one, revoke a leaked one, mint another."
        >
          Keys
        </button>
        {superadmin && (
          <button
            type="button"
            class="ghost"
            onClick={() => go(ADMIN_PATH)}
            title="The operator console: customers, keys, jobs, billing events, usage."
          >
            Console
          </button>
        )}
        <a
          class="ghost help"
          href="https://docs.easting.ai/web/"
          target="_blank"
          rel="noreferrer"
        >
          Help
        </a>
        {busy && (
          <>
            <span class="busy">{busy}</span>
            <button
              type="button"
              class="ghost"
              onClick={() => abort.current?.abort()}
              title="Stops the waiting, not the extraction. The result stays collectable."
            >
              Stop waiting
            </button>
          </>
        )}
        <button
          type="button"
          class="ghost"
          onClick={() => (
            auth.configured ? auth.signOut() : devAuth.signOut(),
            setSignedIn(false)
          )}
        >
          Sign out
        </button>
      </header>

      {error && (
        <div class="error" role="alert">
          {error}
          <button type="button" class="ghost" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      <main class="split">
        <aside class="panel">
          {batch.length > 1 && (
            <Triage
              batch={batch}
              held={held}
              onOpen={(item) => {
                setPlaced([]);
                setHeld(item);
                setLocated(null);
                setFocus(null);
                autoPlaceGeoref(item);
                // Each document of a folder keeps its own positions, so
                // switching documents brings that deed's back rather than
                // the batch's first.
                if (reference) void restorePlacements(reference.id, item);
              }}
            />
          )}
          {pasting ? (
            <PasteText
              onSubmit={extractText}
              onCancel={() => setPasting(false)}
            />
          ) : held ? (
            <>
              <Review
                held={held}
                onPlace={startPlacing}
                onPlaceGeoref={placeGeoref}
                onPlaceSuggested={placeSuggested}
                placedTracts={placedNames}
                // Only where there is something to re-sign. A batch result is
                // sliced out of a collection response and carries no
                // attestation of its own, so offering an edit there would put
                // a button on the page that the service can only refuse.
                onCorrect={held.parsed.attestation ? correct : undefined}
                correcting={correcting}
              />
              <div class="exports">
                <button
                  type="button"
                  onClick={suggestLocations}
                  disabled={locating || !anyLocatable}
                  title={
                    anyLocatable
                      ? "Ask where these tracts sit: a PLSS corner the fabric can resolve, or a boundary shared with a parcel you have already placed. Suggestions are previewed, never placed."
                      : "No tract here needs a suggested location."
                  }
                >
                  {locating ? "Locating…" : "Suggest locations"}
                </button>
                {located !== null && (
                  <span class="muted small">
                    {located
                      ? `${located} tract${located === 1 ? "" : "s"} suggested. Nothing is placed until you confirm.`
                      : "No tract here commences at a PLSS corner the fabric resolves, and none shares a boundary with a parcel you have placed; each card says what was tried."}
                  </span>
                )}
                <button
                  type="button"
                  onClick={saveCertificate}
                  title="The GroundTruth Certificate of Digitization as a PDF: verdicts, each tract drawn to scale, every course numbered against its source text."
                >
                  Certificate…
                </button>
                <button
                  type="button"
                  onClick={saveDxf}
                  disabled={!anyPlaceable}
                  title={
                    anyPlaceable
                      ? "The traverse as a CAD drawing, unplaced by design."
                      : "No tract in this document has computed geometry to draw."
                  }
                >
                  DXF…
                </button>
                <button
                  type="button"
                  onClick={saveGeoPackage}
                  disabled={!anyPlaceable}
                  title={
                    anyPlaceable
                      ? "Tract and call layers as a GeoPackage, written by the server. Local-plane tracts are unplaced; aliquot tracts include a placed WGS 84 layer."
                      : "No tract in this document has computed geometry to write."
                  }
                >
                  GeoPackage…
                </button>
              </div>
            </>
          ) : (
            <Empty busy={!!busy} />
          )}
        </aside>

        <section class="mapside">
          <MapView
            pending={pending}
            placed={placed}
            rotationDeg={rotation}
            pob={pob}
            onPobPicked={setPob}
            focus={focus}
          />
          {pending && (
            <div class="place-bar">
              <label>
                Rotation
                <input
                  type="range"
                  min={-45}
                  max={45}
                  step={0.25}
                  value={rotation}
                  onInput={(event) =>
                    setRotation(
                      Number((event.target as HTMLInputElement).value),
                    )
                  }
                />
                <span class="mono">{rotation.toFixed(2)}°</span>
              </label>
              <button
                type="button"
                class="primary"
                disabled={!pob}
                onClick={confirmPlacement}
              >
                Confirm placement
              </button>
              <button
                type="button"
                class="ghost"
                onClick={() => setPending(null)}
              >
                Cancel
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Empty({ busy }: { busy: boolean }) {
  if (busy) return <p class="muted">Working…</p>;
  return (
    <div class="empty">
      <h2>Read a recorded document</h2>
      <p class="muted">
        Drop in the PDF the county gave you, scan and all. Easting transcribes
        every course with the words it was read from, recomputes the traverse,
        checks closure against the acreage the document states, and only then
        draws it.
      </p>
      <p class="muted small">
        PDF, TIFF, PNG, and JPEG, up to 32 MB. Already holding the description
        as text, from a title commitment or an email? Paste text… runs the same
        checks and the certificate says which source it read. Nothing you send
        is stored after it is read.
      </p>
    </div>
  );
}

/**
 * The paste surface. Deliberately nothing edits the text on the way through:
 * what lands here is what the service reads, hashes, and prints on the
 * certificate as `Source: provided text`, so a helpful trim would change what
 * the paperwork says was read.
 */
function PasteText({
  onSubmit,
  onCancel,
}: {
  onSubmit: (text: string, docName: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const ready = text.trim().length > 0;
  return (
    <div class="empty paste-form">
      <h2>Paste a legal description</h2>
      <p class="muted small">
        Exactly as the document gives it: the point of beginning, every course,
        and the closing call. The result is verified the same way a scanned
        document is, and its certificate says the source was provided text.
      </p>
      <input
        type="text"
        placeholder="Name (optional)"
        value={name}
        onInput={(event) => setName((event.target as HTMLInputElement).value)}
      />
      <textarea
        rows={12}
        placeholder="BEGINNING at an iron stake…"
        value={text}
        onInput={(event) =>
          setText((event.target as HTMLTextAreaElement).value)
        }
      />
      <div class="actions">
        <button
          type="button"
          class={ready ? "primary" : "primary disabled"}
          disabled={!ready}
          onClick={() => onSubmit(text, name.trim() || "Pasted text")}
        >
          Extract
        </button>
        <button type="button" class="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function FilePicker({
  onExtract,
  onBatch,
  onPaste,
  disabled,
}: {
  onExtract: (file: File) => void;
  onBatch: (files: File[]) => void;
  onPaste: () => void;
  disabled: boolean;
}) {
  const pick = (handler: (files: File[]) => void) => (event: Event) => {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    if (files.length) handler(files);
  };
  return (
    <>
      <label class={disabled ? "primary disabled" : "primary"}>
        Open document…
        <input
          type="file"
          accept={ACCEPT}
          disabled={disabled}
          onChange={pick((files) => onExtract(files[0]))}
        />
      </label>
      {/* Its own control rather than multi-select on the one above: nothing
          on screen would otherwise say a batch is possible, and it behaves
          differently enough to deserve saying so. */}
      <label
        class={disabled ? "secondary disabled" : "secondary"}
        title="Several documents at once. They settle together, usually within the hour."
      >
        Extract several…
        <input
          type="file"
          accept={ACCEPT}
          multiple
          disabled={disabled}
          onChange={pick((files) =>
            files.length === 1 ? onExtract(files[0]) : onBatch(files),
          )}
        />
      </label>
      <button
        type="button"
        class={disabled ? "secondary disabled" : "secondary"}
        disabled={disabled}
        onClick={onPaste}
        title="Paste a legal description instead of choosing a file. Same checks; the certificate says the source was provided text."
      >
        Paste text…
      </button>
    </>
  );
}

const ACCEPT = ".pdf,.tif,.tiff,.png,.jpg,.jpeg,.txt";

function describe(failure: unknown): string {
  if (failure instanceof Cancelled) return failure.message;
  if (failure instanceof ApiError) return failure.message;
  if (failure instanceof Error) return failure.message;
  return "Something went wrong.";
}
